/** @format */
import { HighlightColor } from '@mpb-com/feather/components/atoms/heading/types';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import { animated } from '@react-spring/web';
import { t } from 'i18next';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import ChevronLeft from '@toucan/client/global/icons/chevron-left.svg';
import ChevronRight from '@toucan/client/global/icons/chevron-right.svg';

import {
    ConstrainedWrapper,
    ConstrainedWrapperGutter,
} from '@toucan/client/shared/components/constrained-wrapper';
import {
    CarouselStyle,
    useCarousel,
} from '@toucan/client/shared/hooks/use-carousel';

import { ArticleTile } from '../article-tile';
import { BlockTitle } from '../block-title';
import { BlurredArticleTile } from '../blurred-article-tile';
import { styles } from './article-carousel.style';
import { ArticleCarouselProps } from './types';

const StyledArticleCarousel = styled.section`
    ${styles}
`;

export const ArticleCarousel: FunctionComponent<ArticleCarouselProps> = (
    props
) => {
    const { isLoading, articles = [] } = props;

    const {
        carouselRef,
        handlePrev,
        handleNext,
        isPrevDisabled,
        isNextDisabled,
        style,
    } = useCarousel<HTMLUListElement>({
        itemLength: articles.length,
        carouselStyle: CarouselStyle.SLIDE,
    });

    if (isLoading) {
        return (
            <StyledArticleCarousel className="carousel-skeleton">
                <ConstrainedWrapper
                    gutter={ConstrainedWrapperGutter.LARGE_GUTTER}
                >
                    <div className="carousel-chrome">
                        <BlockTitle
                            isLoading
                            showPretitle
                            className="carousel-title"
                        />
                        <div className="carousel-controls">
                            <Skeleton variant={SkeletonVariant.CIRCLE} />
                            <Skeleton variant={SkeletonVariant.CIRCLE} />
                        </div>
                    </div>
                    <ul className="carousel-wrapper" ref={carouselRef}>
                        <ArticleTile className="article-tile" isLoading />
                        <ArticleTile className="article-tile" isLoading />
                        <ArticleTile className="article-tile" isLoading />
                        <ArticleTile className="article-tile" isLoading />
                    </ul>
                </ConstrainedWrapper>
            </StyledArticleCarousel>
        );
    }

    const { title, pretitle, seeAllLink } = props;

    const blurredArticleProps = articles[0];

    const getCarouselItems = () => {
        const carouselItems = articles.map((article) => (
            <ArticleTile
                className="article-tile"
                key={article.title}
                {...article}
            />
        ));

        carouselItems.push(
            <BlurredArticleTile
                className="article-tile"
                key="blurred-article-tile"
                {...blurredArticleProps}
                seeAllLink={seeAllLink}
            />
        );

        return carouselItems;
    };

    return (
        <StyledArticleCarousel>
            <ConstrainedWrapper gutter={ConstrainedWrapperGutter.LARGE_GUTTER}>
                <div className="carousel-chrome">
                    <BlockTitle
                        highlightColor={HighlightColor.TEXT_ACCENT_INVERSE}
                        title={title}
                        pretitle={pretitle}
                        showPretitle
                        className="carousel-title"
                    />
                    <div className="carousel-controls">
                        <Icon
                            size={IconSize.M}
                            variant={IconVariant.NAVIGATION}
                            aria-label={t('Previous')}
                            data-testid="carousel__prev-arrow"
                            onClick={handlePrev}
                            disabled={isPrevDisabled}
                        >
                            <ChevronLeft />
                        </Icon>
                        <Icon
                            size={IconSize.M}
                            variant={IconVariant.NAVIGATION}
                            aria-label={t('Next')}
                            data-testid="carousel__next-arrow"
                            onClick={handleNext}
                            disabled={isNextDisabled}
                        >
                            <ChevronRight />
                        </Icon>
                    </div>
                </div>
                <animated.ul
                    ref={carouselRef}
                    className="carousel-wrapper"
                    style={style}
                >
                    {getCarouselItems()}
                </animated.ul>
            </ConstrainedWrapper>
        </StyledArticleCarousel>
    );
};
